import { useState, useEffect, useRef } from 'react'
import { Search, Check, X } from 'lucide-react'

/**
 * Editor for `editorType: term-ref` fields. Autocomplete over Drupal
 * taxonomy terms via `/api/builder/search/terms?q=...&vocabularies=...`.
 *
 * Value shape :   {tid: int, vocabulary: string, name: string}
 *
 * Props :
 *  - value : current ref ({tid, vocabulary, name}) or null
 *  - vocabularies : array of allowed vocabulary machine names
 *  - onSave, onClose
 */
export const TermRefEditor = ({ value, vocabularies, onSave, onClose }) => {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState(
    value && typeof value === 'object' && value.tid ? value : null,
  )
  const debounceRef = useRef(null)

  const vocabParam = Array.isArray(vocabularies) && vocabularies.length > 0
    ? `&vocabularies=${encodeURIComponent(vocabularies.join(','))}`
    : ''

  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await fetch(
          `/api/builder/search/terms?q=${encodeURIComponent(q)}${vocabParam}&limit=20`,
          { credentials: 'include' },
        )
        if (res.ok) setResults(await res.json())
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 200)
    return () => clearTimeout(debounceRef.current)
  }, [q, vocabParam])

  const handleSave = () => {
    if (selected) onSave(selected)
    onClose()
  }

  const handleClear = () => {
    onSave(null)
    onClose()
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={vocabularies?.length ? `Search ${vocabularies.join(' / ')}…` : 'Search terms…'}
          autoFocus
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500"
        />
      </div>

      {selected && (
        <div className="flex items-center justify-between p-2 bg-teal-50 border border-teal-200 rounded-lg">
          <div className="text-sm">
            <div className="font-medium text-teal-900">{selected.name}</div>
            <div className="text-xs text-teal-700">tid: {selected.tid} · {selected.vocabulary}</div>
          </div>
          <button
            onClick={() => setSelected(null)}
            className="p-1 rounded hover:bg-teal-100"
            aria-label="Remove selection"
          >
            <X size={14} className="text-teal-700" />
          </button>
        </div>
      )}

      <div className="border border-gray-100 rounded-lg max-h-72 overflow-y-auto">
        {loading && <p className="text-xs text-gray-400 text-center py-3">Loading…</p>}
        {!loading && results.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-3">No matches.</p>
        )}
        {!loading && results.map((r) => (
          <button
            key={r.tid}
            type="button"
            onClick={() => setSelected(r)}
            className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between ${
              selected?.tid === r.tid
                ? 'bg-teal-50 text-teal-900'
                : 'hover:bg-gray-50'
            }`}
          >
            <div className="min-w-0 flex-1">
              <div className="truncate">{r.name}</div>
              <div className="text-xs text-gray-400">tid: {r.tid} · {r.vocabulary}</div>
            </div>
            {selected?.tid === r.tid && <Check size={14} className="text-teal-600 flex-shrink-0" />}
          </button>
        ))}
      </div>

      <div className="builder-editor-actions builder-editor-actions-split">
        <button
          onClick={handleClear}
          className="btn btn-sm btn-ghost builder-editor-remove"
        >
          Retirer la référence
        </button>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn btn-secondary">
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={!selected}
            className="btn btn-primary"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  )
}
